import { useEffect, useRef, useState } from "react";
import { campo, insignia } from "./estilos";

export const Etiqueta = ({ clase, children }) => <span className={`${insignia} ${clase}`}>{children}</span>;

export function Barra({ porcentaje, className = "" }) {
  return (
    <div className={`h-1.5 w-full overflow-hidden rounded-full bg-white/10 ${className}`}>
      <span className="block h-full rounded-full bg-white transition-all duration-500" style={{ width: `${porcentaje}%` }} />
    </div>
  );
}

const iniciales = (nombre) =>
  (nombre || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

// Color estable por nombre, como en el portal.
const colorDe = (nombre) => {
  let tono = 0;
  for (const letra of nombre || "?") tono = (tono * 31 + letra.charCodeAt(0)) % 360;
  return `hsl(${tono} 30% 28%)`;
};

export function Avatar({ nombre, tamano = "h-8 w-8 text-[10px]" }) {
  return (
    <span
      title={nombre || "Sin asignar"}
      style={{ backgroundColor: nombre ? colorDe(nombre) : undefined }}
      className={`inline-flex shrink-0 items-center justify-center rounded-full border border-white/15 font-bold uppercase text-white ${nombre ? "" : "bg-zinc-800 text-gray-500"} ${tamano}`}
    >
      {nombre ? iniciales(nombre) : "?"}
    </span>
  );
}

// Ventana modal con el estilo del portal: se cierra con Escape o al hacer clic afuera.
// Si es más alta que la pantalla, se desplaza la ventana (no la página de atrás).
export function Ventana({ titulo, subtitulo, onCerrar, children, ancho = "max-w-2xl" }) {
  useEffect(() => {
    const alPresionar = (e) => e.key === "Escape" && onCerrar();
    window.addEventListener("keydown", alPresionar);
    return () => window.removeEventListener("keydown", alPresionar);
  }, [onCerrar]);

  useEffect(() => {
    const anterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = anterior; };
  }, []);

  const cerrarAfuera = (e) => e.target === e.currentTarget && onCerrar();

  return (
    <div
      className="ventana-fondo fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
      onMouseDown={cerrarAfuera}
    >
      {/* Centrada cuando cabe; cuando no, empieza arriba y se recorre completa. */}
      <div className="flex min-h-full items-center justify-center p-4 sm:p-8" onMouseDown={cerrarAfuera}>
      <div className={`ventana-panel w-full ${ancho} rounded-2xl border border-white/10 bg-zinc-950 p-6 sm:p-8`}>
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            {subtitulo && <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">{subtitulo}</p>}
            <h3 className="text-2xl font-black uppercase tracking-tight">{titulo}</h3>
          </div>
          <button type="button" onClick={onCerrar} aria-label="Cerrar" className="text-2xl leading-none text-gray-500 hover:text-white">×</button>
        </div>
        {children}
      </div>
      </div>
    </div>
  );
}

// Lista desplegable de personas con su avatar y un buscador (hay muchos miembros).
export function SelectorPersona({ id, personas, valor, onCambiar, vacio, deshabilitado }) {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const caja = useRef(null);
  const elegida = personas.find((p) => String(p.id) === String(valor));
  const texto = busqueda.trim().toLowerCase();
  const visibles = texto ? personas.filter((p) => p.nombre.toLowerCase().includes(texto)) : personas;

  useEffect(() => {
    if (!abierto) return undefined;
    const alPresionar = (e) => caja.current && !caja.current.contains(e.target) && setAbierto(false);
    document.addEventListener("mousedown", alPresionar);
    return () => document.removeEventListener("mousedown", alPresionar);
  }, [abierto]);

  const elegir = (persona) => {
    onCambiar(persona ? persona.id : "");
    setAbierto(false);
    setBusqueda("");
  };

  return (
    <div className="relative" ref={caja}>
      <button
        id={id}
        type="button"
        disabled={deshabilitado}
        onClick={() => setAbierto(!abierto)}
        aria-haspopup="listbox"
        aria-expanded={abierto}
        className={`${campo} flex items-center gap-3 text-left`}
      >
        <Avatar nombre={elegida?.nombre} tamano="h-7 w-7 text-[9px]" />
        <span className={`flex-1 truncate ${elegida ? "" : "text-gray-500"}`}>{elegida?.nombre || vacio}</span>
        <span className="text-gray-500">▾</span>
      </button>
      {abierto && (
        <div className="absolute z-10 mt-2 w-full rounded-xl border border-white/15 bg-zinc-900 p-1 shadow-2xl">
          <input
            autoFocus
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar…"
            aria-label="Buscar persona"
            className="mb-1 w-full rounded-lg border border-white/10 bg-zinc-950 px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-white/40 focus:outline-none"
          />
          <ul role="listbox" className="max-h-64 overflow-y-auto">
            <li>
              <button type="button" onClick={() => elegir(null)} className="w-full rounded-lg px-3 py-2 text-left text-sm text-gray-400 hover:bg-white/10">{vacio}</button>
            </li>
            {visibles.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={elegida?.id === p.id}
                  onClick={() => elegir(p)}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-white/10 ${elegida?.id === p.id ? "bg-white/10" : ""}`}
                >
                  <Avatar nombre={p.nombre} />
                  <span className="truncate text-sm font-semibold">{p.nombre}</span>
                </button>
              </li>
            ))}
            {!visibles.length && <li className="px-3 py-2 text-xs text-gray-500">Nadie coincide con la búsqueda.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
